import { POINTS_DAYS_OPTIONS } from '../settings/constants';
import type { AttendanceRule } from '../settings/types';

/**
 * Checks if two time threshold ranges overlap (in minutes).
 * Uses INCLUSIVE comparison for bounded ranges - ranges touching at boundaries count as overlap.
 * This is because an employee exactly at the boundary time (e.g., 10 min late)
 * would match both rules, creating ambiguity.
 *
 * However, when one range is "ABOVE" (max = null, meaning infinity), the lower boundary
 * of the ABOVE range uses EXCLUSIVE comparison. This prevents overlap at the exact boundary
 * between a bounded range ending at X and an ABOVE range starting at X.
 * Example: "Up to 10 minutes" (0-10) and "Above 10 minutes" (10+) do NOT overlap.
 *
 * @param min1 - Start of first range (null means 0)
 * @param max1 - End of first range (null means infinity)
 * @param min2 - Start of second range (null means 0)
 * @param max2 - End of second range (null means infinity)
 * @returns true if time ranges overlap
 */
const doTimeRangesOverlap = (
  min1: number | null,
  max1: number | null,
  min2: number | null,
  max2: number | null
): boolean => {
  const actualMin1 = min1 ?? 0;
  const actualMax1 = max1 ?? Infinity;
  const actualMin2 = min2 ?? 0;
  const actualMax2 = max2 ?? Infinity;

  // if one of the time range is "ABOVE" (max = null), then the comparison is not inclusive for above range
  // we don't need to care about both being null (infinity) as that is already covered in the inclusive comparison
  if (max1 === null && max2 !== null) {
    return actualMin1 < actualMax2 && actualMin2 <= actualMax1;
  }
  if (max2 === null && max1 !== null) {
    return actualMin1 <= actualMax2 && actualMin2 < actualMax1;
  }

  // INCLUSIVE comparison: touching at boundaries counts as overlap
  return actualMin1 <= actualMax2 && actualMin2 <= actualMax1;
};

/**
 * Checks if two appliesToDays values overlap.
 * Day filters use 3 possible values:
 * - "ALL_DAYS" - overlaps with ALL day types (WEEKDAYS, WEEKENDS, and itself)
 * - "WEEKDAYS" - overlaps only with ALL_DAYS and itself
 * - "WEEKENDS" - overlaps only with ALL_DAYS and itself
 * WEEKDAYS and WEEKENDS are mutually exclusive.
 *
 * @param days1 - First day filter value
 * @param days2 - Second day filter value
 * @returns true if day filters overlap
 */
const doDaysOverlap = (days1: string, days2: string): boolean => {
  // If either is ALL_DAYS, they overlap
  if (days1 === POINTS_DAYS_OPTIONS.ALL_DAYS || days2 === POINTS_DAYS_OPTIONS.ALL_DAYS) {
    return true;
  }

  // For WEEKDAYS and WEEKENDS: only overlap if exactly the same
  return days1 === days2;
};

/**
 * Checks if two shift filter ranges overlap.
 * Shift durations use 4 predefined combinations:
 * - "Any shift" (null, null) - overlaps with ALL shift types
 * - "Up to 4 hours" (null, 4) (≤4 hours) - mutually exclusive with (4,8) and (8,null)
 * - "Over 4 hours and up to 8 hours" (4, 8) (>4, ≤8 hours) - mutually exclusive with (null,4) and (8,null)
 * - "Over 8 hours" (8, null) (>8 hours) - mutually exclusive with (null,4) and (4,8)
 *
 * @param min1 - Start of first range
 * @param max1 - End of first range
 * @param min2 - Start of second range
 * @param max2 - End of second range
 * @returns true if shift ranges overlap
 */
const doShiftRangesOverlap = (
  min1: number | null,
  max1: number | null,
  min2: number | null,
  max2: number | null
): boolean => {
  const isRange1Any = min1 === null && max1 === null;
  const isRange2Any = min2 === null && max2 === null;

  // "Any shift" overlaps with everything (including itself)
  if (isRange1Any || isRange2Any) {
    return true;
  }

  // For specific shift ranges (null,4), (4,8), (8,null):
  // They only overlap if they're exactly the same
  return min1 === min2 && max1 === max2;
};

/**
 * Checks if two attendance rules (Late Arrival or Early Departure) overlap or are duplicates.
 *
 * Rules are considered overlapping if ALL of the following conditions are met:
 * 1. Their day filters overlap (ALL_DAYS overlaps with WEEKDAYS/WEEKENDS/itself; WEEKDAYS and WEEKENDS are mutually exclusive)
 * 2. Their time threshold ranges overlap (thresholdMinMinutes to thresholdMaxMinutes)
 * 3. Their shift filter ranges overlap (shiftFilterMinHours to shiftFilterMaxHours)
 *
 * This prevents creation of ambiguous rules where it's unclear which rule applies
 * to a given situation.
 *
 * @param rule1 - First attendance rule
 * @param rule2 - Second attendance rule
 * @returns true if rules overlap/duplicate, false otherwise
 *
 * @example
 * // Exact duplicate
 * checkForOverlappingAttendanceRules(
 *   { thresholdMinMinutes: 5, thresholdMaxMinutes: 10, appliesToDays: 'ALL_DAYS',
 *     shiftFilterMinHours: null, shiftFilterMaxHours: 4 },
 *   { thresholdMinMinutes: 5, thresholdMaxMinutes: 10, appliesToDays: 'ALL_DAYS',
 *     shiftFilterMinHours: null, shiftFilterMaxHours: 4 }
 * ) // true
 *
 * @example
 * // Time ranges overlap, ALL_DAYS overlaps with WEEKDAYS, shifts overlap - OVERLAP
 * checkForOverlappingAttendanceRules(
 *   { thresholdMinMinutes: 5, thresholdMaxMinutes: 15, appliesToDays: 'ALL_DAYS',
 *     shiftFilterMinHours: null, shiftFilterMaxHours: null },
 *   { thresholdMinMinutes: 10, thresholdMaxMinutes: 20, appliesToDays: 'WEEKDAYS',
 *     shiftFilterMinHours: null, shiftFilterMaxHours: null }
 * ) // true (ALL_DAYS overlaps WEEKDAYS, time overlaps 10-15, shifts overlap)
 *
 * @example
 * // WEEKDAYS vs WEEKENDS - NO OVERLAP
 * checkForOverlappingAttendanceRules(
 *   { thresholdMinMinutes: 5, thresholdMaxMinutes: 15, appliesToDays: 'WEEKDAYS',
 *     shiftFilterMinHours: null, shiftFilterMaxHours: null },
 *   { thresholdMinMinutes: 10, thresholdMaxMinutes: 20, appliesToDays: 'WEEKENDS',
 *     shiftFilterMinHours: null, shiftFilterMaxHours: null }
 * ) // false (WEEKDAYS and WEEKENDS are mutually exclusive)
 *
 * @example
 * // Different shift ranges - NO OVERLAP
 * checkForOverlappingAttendanceRules(
 *   { thresholdMinMinutes: 5, thresholdMaxMinutes: 10, appliesToDays: 'ALL_DAYS',
 *     shiftFilterMinHours: null, shiftFilterMaxHours: 4 },
 *   { thresholdMinMinutes: 5, thresholdMaxMinutes: 10, appliesToDays: 'ALL_DAYS',
 *     shiftFilterMinHours: 4, shiftFilterMaxHours: 8 }
 * ) // false (different shift durations: up to 4 vs 4-8, mutually exclusive)
 *
 */
export const checkForOverlappingAttendanceRules = (rule1: AttendanceRule, rule2: AttendanceRule): boolean => {
  // early return for missing required data fields
  // not checking for shifts because (null,null) is a valid value meaning "any shift"
  // also not checking for thresholdMaxMinutes because null means "Above" range option
  if (
    rule1.appliesToDays === null ||
    rule2.appliesToDays === null ||
    rule1.thresholdMinMinutes === null ||
    rule2.thresholdMinMinutes === null
  ) {
    return false;
  }

  // Dimension 1: Days must overlap
  // ALL_DAYS overlaps with everything; WEEKDAYS and WEEKENDS are mutually exclusive
  const daysOverlap = doDaysOverlap(rule1.appliesToDays, rule2.appliesToDays);
  if (!daysOverlap) {
    return false;
  }

  // Dimension 2: Shift filter ranges must overlap
  // Check if the shift hour ranges overlap (shiftFilterMinHours to shiftFilterMaxHours)
  const shiftRangesOverlap = doShiftRangesOverlap(
    rule1.shiftFilterMinHours,
    rule1.shiftFilterMaxHours,
    rule2.shiftFilterMinHours,
    rule2.shiftFilterMaxHours
  );

  if (!shiftRangesOverlap) {
    return false;
  }

  // Dimension 3: Time threshold ranges must overlap
  // Check if the minute ranges overlap (thresholdMinMinutes to thresholdMaxMinutes)
  const timeRangesOverlap = doTimeRangesOverlap(
    rule1.thresholdMinMinutes,
    rule1.thresholdMaxMinutes,
    rule2.thresholdMinMinutes,
    rule2.thresholdMaxMinutes
  );

  if (!timeRangesOverlap) {
    return false;
  }

  // All three dimensions overlap/match - rules are duplicates/overlapping
  return true;
};
